import React, { useState, useCallback, useEffect } from 'react';
import { Link, Modal, PillButton, Row, Select, Stack, Text, TextArea } from 'library/ui';
import { IShift, ShiftProviderUser } from 'models';
import { ILeanShift } from '../../BusinessJob/models';
import { client, consoleLogInDev } from 'shared';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import { DynamicCancellationReason } from '../../models';

enum KnownFormFields {
    SelectAValue = 'SELECT_A_VALUE',
    Other = 'OTHER',
}

const PLACEHOLDER = {
    id: -1,
    key: KnownFormFields.SelectAValue,
    value: 'Select a reason',
} as const;

export default function RemoveProviderFromShfit({
    shift,
    provider,
    provider_id,
    setBanModal,
}: {
    provider: ShiftProviderUser;
    provider_id: number;
    shift: IShift | ILeanShift;
    setBanModal: (b: boolean) => void;
}) {
    const [formFields, setFormFields] = useState<{ key: string; value: string; id: number }[]>([PLACEHOLDER]);
    const [reason, setReason] = useState<string>(PLACEHOLDER.key);
    const [cancelShiftModal, setCancelShiftModal] = useState(false);
    const [comments, setComments] = useState('');
    const [inputError, setInputError] = useState<string | null>(null);

    const fetchCancellationQuestions = useCallback(() => {
        const businessCalendarRemoveProvider = 'BUSINESS_CALENDAR_REMOVE_PROVIDER';

        client(`api/reason-items/?type=${businessCalendarRemoveProvider}`).then((res: DynamicCancellationReason[]) => {
            const items = res.map((item: DynamicCancellationReason) => ({
                key: item.raw_value,
                value: item.display_text,
                id: item.id,
            }));
            setFormFields([PLACEHOLDER, ...items]);
        });
    }, [setFormFields]);

    function removeProvider() {
        const reasonNotSelected = !reason || reason === KnownFormFields.SelectAValue;
        const otherSelected = reason === KnownFormFields.Other;
        const otherWithNoComments = otherSelected && !comments;

        const shouldError = reasonNotSelected || otherWithNoComments;

        if (shouldError) {
            setInputError(`You must provide a reason for removing ${provider.first_name}.`);
            return;
        }

        const reasonId = formFields.find((x) => x.key === reason)?.id;

        client('api/sub-shifts/remove-provider/', {
            body: {
                provider_id: provider_id,
                appointment_id: shift.id,
                reason_id: reasonId,
                comments,
            },
        })
            .then(() => {
                setBanModal(true);
                setInputError(null);
            })
            .catch(consoleLogInDev)
            .finally(() => setCancelShiftModal(false));
    }

    useEffect(() => {
        if (cancelShiftModal) {
            fetchCancellationQuestions();
        }
    }, [fetchCancellationQuestions, cancelShiftModal]);

    return (
        <>
            <Link onClick={() => setCancelShiftModal(true)} icon={<PersonRemoveIcon sx={{ fontSize: 16 }} />}>
                Remove {provider.first_name}
            </Link>
            {cancelShiftModal && (
                <Modal
                    title={`Remove ${provider?.first_name} from this shift?`}
                    onClose={() => setCancelShiftModal(false)}
                >
                    <Stack gap="lg">
                        <Text>
                            We will inform {provider?.first_name} that you no longer need them to work on this day. This
                            action will reopen the shift to be filled by a different provider. If you need to cancel the
                            shift completely, you can select <Text weight="bold">Cancel this shift</Text> instead.
                        </Text>
                        <Select
                            fullWidth
                            value={reason}
                            options={formFields.map((field) => ({ key: field.key, value: field.value }))}
                            onChange={setReason}
                        />
                        <Stack gap="sm">
                            <TextArea
                                fullWidth
                                minRows={5}
                                value={comments}
                                placeholder={
                                    reason === KnownFormFields.Other ? 'Enter Your Reason for Cancelling*' : 'Comments'
                                }
                                onChange={setComments}
                                maxLength={500}
                            />
                            <Text size="xs" tone="secondary">
                                Comments will only be seen by The Tandem Team. 500 Character limit.
                            </Text>
                        </Stack>
                        {inputError && <Text tone="error">{inputError}</Text>}
                        <Row justify="end" gap="md" fullWidth>
                            <PillButton variant="secondary" onClick={() => setCancelShiftModal(false)}>
                                Cancel
                            </PillButton>
                            <PillButton onClick={removeProvider}>Confirm</PillButton>
                        </Row>
                    </Stack>
                </Modal>
            )}
        </>
    );
}
